import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close, Save, Business } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAssignUserFacilities } from '@/api/hooks/users/useAssignUserFacilities';
import CpdActionButton from '@/components/ui/CpdActionButton';
import FacilitiesAutocomplete from '@/components/FacilitiesAutocomplete/FacilitiesAutocomplete';

const AssignFacilitiesDialog = ({ open, onClose, user, onSuccess }) => {
  const { t } = useTranslation();
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);
  const [allFacilities, setAllFacilities] = useState(false);
  const { assignFacilities, loading } = useAssignUserFacilities();

  useEffect(() => {
    if (open) {
      setSelectedFacilityIds([]);
      setAllFacilities(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    try {
      await assignFacilities(user.id, {
        facilityIds: selectedFacilityIds,
        allFacilities,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning facilities:', error);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleAllFacilitiesChange = (event) => {
    setAllFacilities(event.target.checked);
    if (event.target.checked) {
      setSelectedFacilityIds([]);
    }
  };

  const isSubmitDisabled =
    loading || (!allFacilities && selectedFacilityIds.length === 0);

  const isOnboarding = user?.status === 2;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business color='primary' />
            <Typography variant='h6'>
              {t('internalUsers.assignFacilities.title')}
            </Typography>
          </Box>
          <Tooltip title={t('common.close')}>
            <IconButton onClick={handleClose} size='small' disabled={loading}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant='body1' gutterBottom>
            <strong>{t('internalUsers.assignFacilities.user')}:</strong>{' '}
            {user?.name}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {user?.email}
          </Typography>
        </Box>

        {isOnboarding && (
          <Alert severity='info' sx={{ mb: 3 }}>
            <Typography variant='body2'>
              {t('internalUsers.assignFacilities.onboardingInfo')}
            </Typography>
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={allFacilities}
              onChange={handleAllFacilitiesChange}
              disabled={loading}
            />
          }
          label={t('internalUsers.assignFacilities.allFacilities')}
          sx={{ mb: 2 }}
        />

        {!allFacilities && (
          <FacilitiesAutocomplete
            value={selectedFacilityIds}
            onChange={setSelectedFacilityIds}
            organizationId={user?.organization_id}
            label={t('internalUsers.assignFacilities.selectFacilities')}
            placeholder={t(
              'internalUsers.assignFacilities.facilitiesPlaceholder',
            )}
            disabled={loading}
            required
            showBulkActions
          />
        )}

        {allFacilities && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            <Typography variant='body2'>
              {t('internalUsers.assignFacilities.allFacilitiesWarning')}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <CpdActionButton
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={loading}
          startIcon={<Save sx={{ color: 'white' }} />}>
          {t('internalUsers.assignFacilities.assign')}
        </CpdActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFacilitiesDialog;
